import { THEME_A_TOKENS } from "../../design-system/v2/foundations/tokens";

// Les palettes de catégories sont des couleurs IMPOSÉES du produit : leur
// source de vérité vit dans les tokens du design system (thémables via
// --ak-v2-category-*). Ce module reste l'API historique de lecture.
const TOKEN_COLORS = THEME_A_TOKENS.colors;

function getColorFromCategory(category) {
  switch (category) {
    case 1:
      return TOKEN_COLORS.categoryQuestion1;
    case 2:
      return TOKEN_COLORS.categoryQuestion2;
    case 3:
      return TOKEN_COLORS.categoryQuestion3;
    case 4:
      return TOKEN_COLORS.categoryQuestion4;
    default:
      return TOKEN_COLORS.categoryQuestionDefault;
  }
}

function getFadeColorFromCategory(category) {
  switch (category) {
    case 1:
      return TOKEN_COLORS.categoryQuestion1Fade;
    case 2:
      return TOKEN_COLORS.categoryQuestion2Fade;
    case 3:
      return TOKEN_COLORS.categoryQuestion3Fade;
    case 4:
      return TOKEN_COLORS.categoryQuestion4Fade;
    default:
      return TOKEN_COLORS.categoryQuestionDefaultFade;
  }
}

// Colors for analytical categories (behav1, behav2, etc.)
function getColorFromAnalyticalCategory(categoryId) {
  const analyticalColors = {
    behav1: "#FF6666", // Alimentation
    behav2: "#990000", // Comportement indésirable
    behav3: "#0099FF", // Exploration et déplacement
    behav4: "#333333", // Indéterminé
    behav5: "#73D2A4", // Interaction humain-animal
    behav6: "#330099", // Repos
    behav7: "#73D2A4", // Interaction observateur - visiteurs
    behav8: "#FD9A4E", // Interactions autres espèces
    behav9: "#000000", // Non visible
    behav10: "#9933FF", // Observation
    behav11: "#FFFCCC", // Social - indéterminé
    behav12: "#CC9900", // Social - négatif
    behav13: "#FFCC33", // Social - positif
    behav14: "#CCCCFF", // Toilettage et maintenance
    behav15: "#660FB5", // Vigilance
  };

  return analyticalColors[categoryId] || TOKEN_COLORS.categoryQuestionDefault;
}

// Fade colors for analytical categories
function getFadeColorFromAnalyticalCategory(categoryId) {
  const color = getColorFromAnalyticalCategory(categoryId);
  // Convert hex to rgba with 0.5 opacity for fade effect
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, 0.5)`;
}

// Classic category colors (cat1, cat2, etc.) - aligned with ethogram (obsAnalytics._getCatColor)
function getColorFromClassicCategory(categoryId) {
  const classicColors = {
    cat1: TOKEN_COLORS.categoryEthogram1, // Dirigé vers l'observateur
    cat2: TOKEN_COLORS.categoryEthogram2, // Exploration et déplacements
    cat3: TOKEN_COLORS.categoryEthogram3, // Comportements sociaux
    cat4: TOKEN_COLORS.categoryEthogram4, // Comportements alimentaires
    cat5: TOKEN_COLORS.categoryEthogram5, // Autres comportements individuels
    cat6: TOKEN_COLORS.categoryEthogram6, // Vocalisations
    cat0: TOKEN_COLORS.categoryEthogram0, // Autre
  };

  return classicColors[categoryId] || TOKEN_COLORS.categoryEthogram5;
}

// Fade colors for classic categories
function getFadeColorFromClassicCategory(categoryId) {
  const color = getColorFromClassicCategory(categoryId);
  // Convert hex to rgba with 0.5 opacity for fade effect
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, 0.5)`;
}

// Universal color getter that works with both category types
function getColorFromCategoryId(categoryId, isAnalytical = false) {
  if (isAnalytical) {
    return getColorFromAnalyticalCategory(categoryId);
  } else {
    return getColorFromClassicCategory(categoryId);
  }
}

// Universal fade color getter
function getFadeColorFromCategoryId(categoryId, isAnalytical = false) {
  if (isAnalytical) {
    return getFadeColorFromAnalyticalCategory(categoryId);
  } else {
    return getFadeColorFromClassicCategory(categoryId);
  }
}

// Specific behavior colors based on French names
function getColorFromBehaviorName(behaviorName) {
  const behaviorColors = {
    // ALIMENTATION
    Boit: "#D15353",
    "Manipule un aliment": "#FF7474",
    "Manipule un aliment ou objet contenant de la nourriture": "#FF7474",
    "Manipule un aliment ou un objet contenant de la nourriture": "#FF7474",
    "Manipule un objet contenant de la nourriture": "#FF7474",
    "Transporte un aliment": "#FF7474",
    Constriction: "#F36161",
    "Essaye d'atteindre de la nourriture inaccessible": "#F36161",
    Mange: "#FFB3B3",
    "Mange du branchage": "#DC5858",
    "Mange du foin": "#FF8282",
    "Mange du fourrage": "#FFC7C7",
    Broute: "#C54F4F",
    "Broute de lherbe": "#C54F4F",
    "Mange de la gomme": "#FFCACA",
    "Mange des excréments": "#FFEAEA",
    "Mange autre": "#B94A4A",
    "Mange de la nourriture donnée par les visiteurs": "#FFB3B3",
    "Mange dans l'enclos": "#FFD5D5",
    "Mange du foin / de l'herbe à proximité d'un congénère": "#E85D5D",
    "Mange autre à proximité d'un congénère": "#FFF8F8",
    "Mange en protégeant sa nourriture": "#FFC0C0",
    "Cache de la nourriture": "#D15353",
    "Cache des aliments": "#D15353",
    "Cache sa nourriture": "#D15353",
    "Mange des caecotrophes": "#FF8989",
    "Lèche une pierre à sel": "#FFF1F1",
    Tète: "#FF7B7B",
    "Mange (ex : légumes, insectes)": "#FFB3B3",
    "Manipule un aliment (ou un objet contenant de la nourriture)": "#FF7474",
    "Mange des éléments anormaux (substrat, bois, excrément, etc.)": "#990000",

    // EXPLORATION ET DEPLACEMENTS
    Marche: "#0076C4",
    "Marche / court": "#0076C4",
    "Marche, court": "#0076C4",
    "Marche au sol": "#0076C4",
    "Marche, court au sol": "#0076C4",
    "Se déplace": "#0076C4",
    "Se déplace au sol": "#0076C4",
    "Ondule lentement": "#0076C4",
    "Marche, court en hauteur": "#005289",
    "Marche sur un agrès": "#005289",
    "Se déplace en hauteur": "#005289",
    "Se déplace en bondissant": "#80CCFF",
    "Se déplace par brachiation": "#80CCFF",
    "Court, marche": "#0076C4",
    "Court, Marche": "#0076C4",
    Galope: "#C4E7FF",
    Court: "#C4E7FF",
    "Court / sautille": "#C4E7FF",
    "Court ou trottine": "#C4E7FF",
    "Court, trottine": "#C4E7FF",
    "Trotte ou galope": "#C4E7FF",
    "Trotte, galope": "#C4E7FF",
    "Ondule rapidement": "#C4E7FF",
    Grimpe: "#007CCE",
    "Grimpe / Se hisse sur une structure": "#007CCE",
    "Grimpe ou se met sur ses pattes arrière": "#007CCE",
    "Grimpe, escalade": "#007CCE",
    "Grimpe, saute": "#007CCE",
    "Grimpe, saute, escalade": "#27A9FF",
    "Grimpe, saute, escalade, se suspend": "#27A9FF",
    "S'accroche, se suspend par la queue": "#27A9FF",
    "S'accroche": "#27A9FF",
    " S'accroche, se suspend, bouge mais sur place": "#27A9FF",
    "S'accroche, se suspend, s'accroche mais sur place": "#27A9FF",
    "Se suspend": "#27A9FF",
    Saute: "#003558",
    "Saute en nageant à la surface de l'eau": "#003558",
    "Saute ou bondit": "#003558",
    "Saute ou grimpe": "#003558",
    "Saute, bondit": "#003558",
    "Saute, grimpe": "#003558",
    "Saute, grimpe, escalade": "#003558",
    Nage: "#9DD8FF",
    "Nage, patauge": "#9DD8FF",
    "Nage, plonge": "#9DD8FF",
    "Nage, plonge (immersion complète)": "#9DD8FF",
    "Nage, plonge ou marche au fond de l'eau": "#9DD8FF",
    Patauge: "#4EB8FF",
    "Patauge dans de l'eau à faible profondeur": "#4EB8FF",
    "Se baigne, patauge": "#4EB8FF",
    "Se déplace dans l'eau, patauge": "#4EB8FF",
    "Se déplace ou patauge dans l'eau": "#4EB8FF",
    Plonge: "#006AB1",
    "Plonge dans l'eau": "#006AB1",
    Vole: "#62C0FF",
    "Vole / plane": "#62C0FF",
    "Entre  dans le bâtiment": "#002945",
    "Entre à l'intérieur": "#002945",
    "Rentre à l'intérieur": "#002945",
    "Va dans la grande partie (Passe sous le fil)": "#002945",
    "Sort à l'extérieur": "#F5FBFF",
    "Va à l'extérieur": "#F5FBFF",
    "Va dans l'enclos": "#F5FBFF",
    "Va dans la petite partie (passe sous le fil)": "#F5FBFF",
    "Se couche": "#0087E2",
    "Se lève": "#D8EFFF",
    "Se repositionne": "#D8EFFF",
    "Se perche": "#B1E0FF",
    Exploration: "#00416C",
    Explore: "#00416C",
    "Explore - Creuse - Fouille le substrat ou la végétation": "#00416C",
    "Explore / Fouille le substrat ou la végétation": "#00416C",
    "Explore, joue": "#00416C",
    "Explore au sol": "#00416C",
    "Explore en hauteur": "#76C8FF",
    "Interaction avec un marquage": "#E2F3FF",
    Marque: "#E2F3FF",
    "Fouille le bassin": "#3BB1FF",
    "Fouille le substrat": "#3BB1FF",
    "Fouille le substrat ou la végétation": "#3BB1FF",
    "Manipule de la végétation": "#3BB1FF",
    Creuse: "#005E9D",
    "Creuse, fouille le substrat ou la végétation": "#005E9D",
    "Gratte, creuse": "#005E9D",
    "Bondit avec les pattes avant": "#14A1FF",
    "Saute à la verticale (tête en bas)": "#14A1FF",
    Chasse: "#93D4FF",
    "Chasse, à l'affût": "#93D4FF",
    "Construction d'un nid": "#0076C4",
    "Construit / aménage un nid": "#0076C4",
    "Construit un nid": "#0076C4",
    "Construit, aménage un nid": "#0076C4",
    "Interagit avec un objet non alimentaire": "#00233B",
    "Joue seul": "#00233B",
    "Joue seul ou manipule un objet non alimentaire": "#00233B",
    "Manipule un objet non alimentaire": "#00233B",
    "Manipule un objet non alimentaire ou joue seul": "#00233B",
    "Manipule un objet non alimentaire ou joue seul ou": "#00233B",
    "Transporte un objet non alimentaire": "#00233B",

    // COMPORTEMENTS INDESIRABLES
    "Se déplace de façon répétitive": "#8B0000",
    "Se déplace de façon répétitive sur le sol ou dans l'eau": "#8B0000",
    "Lèche une surface": "#F3E2E2",
    Mâche: "#4D0000",
    "Mâche à vide": "#4D0000",
    "Mastication - non associée à l'alimentation": "#4D0000",
    Salive: "#D08989",
    "Se lèche les lèvres": "#C06262",
    "Mange des éléments anormaux": "#8B0000",
    "Boit de l'urine": "#E7C4C4",
    Régurgitation: "#A51D1D",
    "Régurgitation ou vomissement": "#A51D1D",
    Régurgite: "#A51D1D",
    "Regurgite et ré-ingère": "#A51D1D",
    "Régurgite et réingère": "#A51D1D",
    "Auto-agression": "#A51D1D",
    Autodirigé: "#A51D1D",
    "Auto-dirigés": "#A51D1D",
    "Autodirigés et/ou ronge les barreaux": "#A51D1D",
    "Mord les barreaux, le grillage, etc.": "#C87676",
    "Mord ou lèche": "#C87676",
    "Mord ou lèche une surface": "#C87676",
    "Mordille le grillage": "#C87676",
    "Mouvements de tête répétitif, balancement": "#DCA7A7",
    "Mouvements de tête répétitifs, balancement": "#DCA7A7",
    Masturbation: "#640000",
    "Monte un objet": "#AD3131",
    "S'accroche ou se jette au grillage": "#A92727",
    "Saute au grillage, à la vitre": "#A92727",
    "Posture figée": "#8D0000",
    "Respire bouche ouverte": "#B13B3B",
    "Mange en se balançant de manière répétitive": "#580000",
    "Chasse sa queue": "#F3E2E2",
    "Se frotte le nez": "#E0B1B1",
    "Feint la mort": "#F3E2E2",
    "Gratte le sol, une grille": "#B84E4E",
    "Interaction aux parois": "#B84E4E",
    "Tire à la longe": "#D49393",
    "Toilettage mutuel - vigoureux": "#D49393",
    "Se déplace de façon répétitive en portant un jeune": "#D49393",
    "Autres comportements anormaux": "#410000",

    // REPOS
    Inactif: "#220955",
    "Inactif / Repos": "#220955",
    "Inactif, repos": "#220955",
    "Inactif, Repos": "#220955",
    "Inactif, repos, rumine": "#220955",
    "Repos / inactif": "#220955",
    Repos: "#220955",
    "Inactif, repos (couché)": "#220955",
    "Inactif, repos (debout)": "#2F0289",
    "Se prélasse sous une lampe chauffante": "#4314A1",
    "Repos sur le côté": "#4314A1",
    "Repos sternal": "#6A45B4",
    Couve: "#6A45B4",
    "Fait un bain de soleil": "#C0B1E0",

    // OBSERVATION
    Observe: "#C081FF",
    "Observe l'observateur": "#66BA91",
    "Observe (environnement, congénères)": "#C081FF",

    // VIGILANCE
    Vigilant: "#660FB5",
    "Vigilant (congénères, environnement)": "#660FB5",
    "Vigilant (environnement, congénères)": "#660FB5",
    "Vigilant (environnement, congénères, autres espèces)": "#660FB5",
    "Vigilant envers l'environnement ou ses congénères": "#660FB5",
    "Vigilant envers l'observateur": "#2B064D",
    "Vigilant vers l'observateur": "#2B064D",
    "Vigilant envers les visiteurs": "#782BBE",
    "Vigilant envers un humain": "#430A76",
    Sursaute: "#5A0DA0",
    "Immobile (freeze)": "#4E0C8B",

    // INTERACTION HUMAIN-ANIMAL
    "Comportement dirigé vers les visiteurs": "#C4ECD9",
    "Dirigé vers les humains": "#C4ECD9",
    "Dirigé vers les visiteurs": "#C4ECD9",
    "Dirigé vers les visiteurs / soigneurs": "#C4ECD9",
    "Dirigé vers l'humain": "#C4ECD9",
    "Dirigé vers l'humain (hors observateur)": "#C4ECD9",
    "Dirigés vers humain": "#C4ECD9",
    "Dirigés vers les humains": "#C4ECD9",
    "Observe un humain": "#1B3026",
    "Observe un soigneur/fauconnier": "#9EE0C0",
    "Observe un visiteur": "#B4E7CE",
    "Positif vers l'observateur": "#C9EEDC",
    "POSITIF vers un soigneur/fauconnier": "#D9F3E7",
    "POSITIF vers un visiteur": "#EFFAF5",
    "Sollicitation observateur": "#93DCB9",
    "Sollicite l'observateur": "#93DCB9",
    Sollicite: "#1B3026",
    "Sollicite les visiteurs": "#1B3026",
    "Mange de la nourriture donnée par les visiteurs": "#93DCB9",
    Fuit: "#66BA91",
    "Fuit l'observateur": "#66BA91",
    "Fuit, se cache": "#66BA91",
    "Fuit / se cache": "#66BA91",
    "Fuit les visiteurs": "#58A27E",
    "Négatif vers l'observateur": "#4B896B",
    "NEGATIF vers soigneur/fauconnier": "#4B896B",
    "NEGATIF vers un visiteur": "#3E7158",
    "Menace les visiteurs": "#3E7158",
    Menace: "#35614C",
    "Menace l'observateur": "#35614C",
    "Menace ou agresse l'observateur": "#35614C",
    "Lipsmacking vers l'observateur": "#2C513F",
    "Touche ou pince le doigt d'un soigneur/fauconnier": "#234132",
    "Touche ou pince le doigt d'un visiteur": "#93DCB9",

    // TOILETTAGE ET MAINTENANCE
    "Se toilette": "#8484A3",
    "Se toilette, se gratte, bâille": "#8484A3",
    "Se toilette, se gratte, se renifle, s'étire": "#8484A3",
    "Se toilette, se gratte, s'étire, bâille": "#8484A3",
    "Se toilette, s'ébroue, se gratte, s'étire": "#8484A3",
    "Se toilette, s'étire": "#8484A3",
    "Se toilette, s'étire, bâille": "#8484A3",
    "Se toilette, s'étire, se gratte": "#8484A3",
    "Se toilette, s'étire, se gratte, baille": "#8484A3",
    "Se toilette, s'étire, se secoue": "#8484A3",
    "S'étire": "#666680",
    "S'étire, bâille": "#666680",
    "S'étire, se gratte, bâille": "#666680",
    "Grattage, toilettage du crâne": "#DEDEFF",
    "Se gratte": "#DEDEFF",
    "Se toilette ou se gratte le crâne": "#DEDEFF",
    "Frotte sa corne contre une structure": "#9D9DC4",
    "Frotte ses cornes sur une structure": "#9D9DC4",
    "Se frotte  sur une structure": "#373745",
    "Se frotte sur une structure": "#373745",
    "Se gratte sur une structure": "#373745",
    "Se gratte, peut être sur une structure": "#373745",
    "Se roule": "#E9E9FF",
    "Se roule, fait ses griffes": "#E9E9FF",
    "Se roule, fait ses griffes, mordille / fait ses dents": "#E9E9FF",
    "Se roule, se frotte": "#E9E9FF",
    "Se roule, se frotte, fait ses griffes": "#E9E9FF",
    "Baille, s'étire": "#F7F7FF",
    "Comportement de thermorégulation": "#D2D2FF",
    "Se rafraîchit": "#D2D2FF",
    "Se réchauffe": "#7E7E9D",
    Frissonne: "#7E7E9D",
    Eternue: "#7E7E9D",
    "Prend un bain": "#EFEFFF",
    "Prends un bain": "#EFEFFF",
    "Se baigne": "#EFEFFF",
    "Se baigne, s'étire, se toilette, bâille": "#EFEFFF",
    "Prend un bain de boue": "#4E4E62",
    "Prend un bain de poussière": "#B4B4E2",
    "Prend un bain de poussière ou de boue": "#B4B4E2",
    "Prend un bain de soleil": "#D8D8FF",
    "Bat des ailes": "#FBFBFF",
    "Se met en boule": "#FBFBFF",
    "Mâche / ronge": "#FBFBFF",
    "Comportement lié aux insectes": "#18181D",
    "Urine, défèque": "#F3F3FF",
    Urine: "#F3F3FF",
    Défèque: "#F3F3FF",
    "Projette de l'urine": "#D6D6FF",
    "Fait du marquage": "#8D8DB1",
    Marquage: "#8D8DB1",
    "Marquage ": "#8D8DB1",
    "Fait ses griffes": "#272731",
    "Se jette du fourrage dessus": "#E2E2FF",
    "Se masturbe": "#E6E6FF",
    Tombe: "#272731",
    Baille: "#F7F7FF",
    Bâille: "#F7F7FF",
    "Se toilette, s'étire": "#8484A3",

    // SOCIAL POSITIF
    "Positif SANS contact": "#FFB747",
    "Positif sans contact": "#FFB747",
    "Positif sans contact ": "#FFB747",
    "Positif AVEC contact": "#FFE081",
    "Positif avec contact": "#FFE081",
    "Positif AVEC ou SANS contact": "#FFE081",
    "Positif (avec ou sans contact)": "#FFE081",
    "Repos social (debout)": "#FFE7A1",
    "Repos  social": "#FFF3D0",
    "Repos en contact social": "#FFF3D0",
    "Repos en contact social social": "#FFF3D0",
    "Repos social": "#FFF3D0",
    "Repos social (couché)": "#FFF3D0",
    "Repos social - 0 à 1 m": "#FFF3D0",
    "Repos social - entre 0 et 1m": "#FFF3D0",
    "Repos social - entre 1 et 3m": "#FFD65A",
    "Repos social - 0 à 5 m": "#EBBC2F",
    Jeu: "#FFB747",
    "Jeu avec congénères": "#FFB747",
    "Joue avec congénères": "#FFB747",
    "S'approche": "#FFFBEF",
    Suit: "#FFEFC0",
    Ronronnement: "#FFE491",
    "Comportement de rut (ex : flehmen)": "#FFD24B",
    "Présente la zone ano-génitale": "#D8AD2B",
    "Sent la zone ano-génitale": "#A78521",
    "Sent une autre partie du corps": "#80661A",
    "Partage d'objet ou de nourriture": "#FFD862",
    "Offre un objet ou de la nourriture": "#FFD862",
    "Fait un croche-patte": "#6C5616",
    "Toilettage mutuel - normal": "#FFE9A9",
    "Lipsmacking display": "#FFF9E7",
    "Se roule devant un autre individu": "#FFE081",
    Lordose: "#45370E",
    Accouplement: "#93761D",
    "Accouplement / Monte": "#FFCC33",
    "Accouplement, monte": "#FFCC33",
    "Reproduction - Accouplement / monte": "#FFCC33",

    // SOCIAL NEGATIF
    "Négatif SANS contact": "#8B6A06",
    "Négatif sans contact": "#8B6A06",
    "Négatif (avec ou sans contact)": "#4E3B00",
    "Négatif AVEC contact": "#4E3B00",
    "Négatif avec contact": "#4E3B00",
    "Négatif AVEC ou SANS contact": "#4E3B00",
    "Provoque l'évitement ou la soumission": "#181200",
    "Évite un congénère ou se soumet": "#D2A51D",
    "Evite un congénère": "#D2A51D",
    "Evite un congénère ou se soumet": "#D2A51D",
    "Evitement - soumission": "#D2A51D",
    "Vol d'objet ou de nourriture": "#100C00",
    "Se fait voler de la nourriture ou un objet": "#BC8D00",
    Grognement: "#957000",
    Sifflement: "#664D00",
    "Cri d'alerte / d'alarme": "#664D00",
    Gémissement: "#4E3B00",
    "Frappe le sol - crache": "#3F2F00",

    // INTERACTION AUTRES ESPECES
    "Positif vers d'autres espèces": "#E25509",
    "POSITIF vers d'autres espèces": "#E25509",
    "Dirigés vers d'autres espèces": "#E25509",
    "Dirigé vers une autre espèce": "#E25509",
    "NÉGATIF vers d'autres espèces": "#D98545",
    "NEGATIF vers d'autres espèces": "#D98545",
    "Indéterminé vers d'autres espèces": "#FDD8B3",

    // INDETERMINE
    "Comportement indéterminé": "#7A7A7A",
    "Comportement indéterminé / hors liste": "#7A7A7A",
    Indéterminé: "#7A7A7A",
    "Comportement hors liste": "#252525",
    "Hors liste": "#252525",
    Aboiement: "#D0D0D0",
    "Chuinte - début": "#B8B8B8",
    "Chuinte - fin": "#A1A1A1",
    Couinement: "#818181",
    "Stutter/stutter-bark": "#727272",
    Rattle: "#727272",
    Piaillement: "#626262",
    Miaulement: "#525252",
    "Percussion avec le bec indéterminé": "#434343",
    "Vocalisation indéterminée": "#2F2F2F",
    "Enroulement de la queue": "#2B2B2B",
    "Exposition du pénis": "#212121",
    "Fin mise en contact": "#121212",
    Flehmen: "#919191",
    "Flotte sans bouger à la surface de l'eau": "#EFEFEF",
    "Mâche-salive-piétine": "#1D1D1D",
    "Gratte l'abri": "#1D1D1D",
    "Se cache": "#C0C0C0",
    "Se cache la tête": "#252525",

    // NON VISIBLE
    "Non visible": "#000000",
  };

  return behaviorColors[behaviorName] || null;
}

export {
  getColorFromCategory,
  getFadeColorFromCategory,
  getColorFromAnalyticalCategory,
  getFadeColorFromAnalyticalCategory,
  getColorFromClassicCategory,
  getFadeColorFromClassicCategory,
  getColorFromCategoryId,
  getFadeColorFromCategoryId,
  getColorFromBehaviorName,
};
